/** Read the final editorial decision without mistaking an old draft for delivered speech. */
import {
  readDirectorReview,
  type DirectorReviewSummary,
} from "./narrationDirectorReview.js";
type Row = Record<string, unknown>;
const row = (value: unknown): Row | null =>
  value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Row)
    : null;
const rows = (value: unknown): Row[] =>
  Array.isArray(value)
    ? value.map(row).filter((v): v is Row => v !== null)
    : [];

export type NarrationPlanSummary = {
  digest: string;
  revision: number;
  deliveryStatus: string;
  directorReviews: DirectorReviewSummary[];
  directorReviewHistory: DirectorReviewSummary[];
  direction: {
    pointOfView: string;
    throughline: string;
    beats: {
      id: string;
      scenes: number[];
      intent: string;
      connection: string;
      lines: string[];
    }[];
  } | null;
  groups: { id: number; scenes: number[]; reason: string; silent: boolean }[];
  phrases: {
    unitId: string;
    text: string;
    scenes: number[];
    start: number;
    end: number;
  }[];
  repairs: { unitId: string; action: string }[];
};

export function readNarrationPlan(
  manifest: unknown,
  expected?: { workloadId?: string | null; finalVideoKey?: string | null },
): NarrationPlanSummary | null {
  const body = row(manifest);
  if (
    (expected?.workloadId && body?.workload_id !== expected.workloadId) ||
    (expected?.finalVideoKey &&
      body?.final_video_s3_key !== expected.finalVideoKey)
  )
    return null;
  const plan = row(row(body?.resolved_script)?.narration_plan);
  const narration = row(body?.narration);
  const compiled = row(narration?.compiled);
  if (
    plan?.schema_version !== "narration-plan.v1" ||
    typeof plan.plan_digest !== "string" ||
    compiled?.plan_digest !== plan.plan_digest
  )
    return null;
  const offset =
    typeof body?.delivered_offset_seconds === "number"
      ? body.delivered_offset_seconds
      : 0;
  const numbers = (value: unknown) =>
    Array.isArray(value)
      ? value.filter(
          (n): n is number => typeof n === "number" && Number.isFinite(n),
        )
      : [];
  const direction = row(row(plan.story_context)?.direction);
  const units = rows(plan.units);
  return {
    digest: plan.plan_digest,
    revision: typeof plan.revision === "number" ? plan.revision : 1,
    deliveryStatus: String(
      row(narration?.delivery_review)?.status ?? "unverified",
    ),
    directorReviews: [
      readDirectorReview(
        row(plan.story_context)?.editorial_review,
        "editorial",
      ),
      readDirectorReview(narration?.director_review, "measured_sequence", {
        planDigest: plan.plan_digest,
        compiledDigest: compiled.digest,
      }),
    ],
    directorReviewHistory: rows(row(plan.story_context)?.review_history)
      .filter(
        (review) =>
          review.scope === "editorial" || review.scope === "measured_sequence",
      )
      .map((review) =>
        readDirectorReview(
          review,
          review.scope as DirectorReviewSummary["scope"],
        ),
      ),
    direction:
      direction &&
      typeof direction.throughline === "string" &&
      typeof direction.point_of_view === "string"
        ? {
            pointOfView: direction.point_of_view,
            throughline: direction.throughline,
            beats: rows(direction.beats).map((beat) => {
              const scenes = numbers(beat.scene_ids);
              return {
                id: String(beat.beat_id ?? ""),
                scenes,
                intent: String(beat.intent ?? ""),
                connection: String(beat.connection ?? ""),
                lines: units
                  .filter((unit) => {
                    const support = numbers(unit.scene_ids);
                    return (
                      support.length > 0 &&
                      support.every((id) => scenes.includes(id))
                    );
                  })
                  .map((unit) => String(unit.text ?? "")),
              };
            }),
          }
        : null,
    groups: rows(plan.groups).map((g) => ({
      id: Number(g.group_id),
      scenes: numbers(g.scene_ids),
      reason: String(g.reason ?? ""),
      silent: g.silent === true,
    })),
    phrases: rows(compiled.phrases)
      .filter((p) => typeof p.start === "number" && typeof p.end === "number")
      .map((p) => ({
        unitId: String(p.unit_id),
        text: String(p.text),
        scenes: numbers(p.scene_ids),
        start: Number(p.start) + offset,
        end: Number(p.end) + offset,
      })),
    repairs: rows(plan.repair_history).map((r) => ({
      unitId:
        typeof r.unit_id === "string"
          ? r.unit_id
          : rows(r.changes)
              .map((change) => String(change.unit_id ?? ""))
              .filter(Boolean)
              .join(", "),
      action: String(r.action),
    })),
  };
}
